import { ArrowLeft } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ActionDropdown } from '../components/ActionDropdown';
import { Badge } from '../components/Badge';
import { ConfirmDialog } from '../components/forms/ConfirmDialog';
import { ForceAssignModal } from '../components/forms/ForceAssignModal';
import { Tabs } from '../components/Tabs';
import {
  useDeleteUser,
  useRemoveUserFromOrg,
  useResetPassword,
  useUpdateUserFull,
  useUserDetail,
} from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

function roleBadgeVariant(role: string): 'default' | 'info' | 'success' | 'primary' | 'warning' {
  switch (role.toLowerCase()) {
    case 'admin':
      return 'primary';
    case 'manager':
      return 'info';
    case 'sales_rep':
      return 'success';
    default:
      return 'default';
  }
}

export function UserDetail({ id }: { id: string }) {
  const { data, isLoading } = useUserDetail(id);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-border rounded w-48 animate-pulse" />
        <div className="h-24 bg-border rounded animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-text-muted">User not found</p>
        <button
          onClick={() => navigate('/users')}
          className="mt-4 text-primary hover:text-primary-hover transition-colors text-sm"
        >
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/users')}
        className="flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Users
      </button>

      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold">{data.fullName}</h1>
        <Badge variant={roleBadgeVariant(data.role)}>{data.role}</Badge>
        {data.platformRole && (
          <Badge variant="primary">{data.platformRole.replace('_', ' ')}</Badge>
        )}
        <Badge variant={data.isActive ? 'success' : 'danger'}>
          {data.isActive ? 'Active' : 'Disabled'}
        </Badge>
      </div>

      <p className="text-sm text-text-secondary">{data.email}</p>

      <div className="bg-surface rounded-xl border border-border p-5">
        <Tabs
          tabs={[
            {
              key: 'organizations',
              label: 'Organizations',
              content: (
                <OrganizationsTab userId={id} userName={data.fullName} orgs={data.organizations} />
              ),
            },
            {
              key: 'settings',
              label: 'Settings',
              content: (
                <UserSettingsTab
                  userId={id}
                  user={data}
                  orgCount={data.organizations?.length ?? 0}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}

function OrganizationsTab({
  userId,
  userName,
  orgs,
}: {
  userId: string;
  userName: string;
  orgs: Array<{ id: string; name: string; role: string }>;
}) {
  const removeFromOrg = useRemoveUserFromOrg();
  const [assignOpen, setAssignOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ orgId: string; orgName: string } | null>(null);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setAssignOpen(true)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          Assign to Organization
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th className="pb-3 pr-4 font-medium">Organization</th>
              <th className="pb-3 pr-4 font-medium">Role</th>
              <th className="pb-3 font-medium w-10" />
            </tr>
          </thead>
          <tbody>
            {orgs.length > 0 ? (
              orgs.map((org) => (
                <tr key={org.id} className="border-b border-border">
                  <td className="py-3 pr-4">
                    <button
                      onClick={() => navigate(`/organizations/${org.id}`)}
                      className="text-text font-medium hover:text-primary transition-colors"
                    >
                      {org.name}
                    </button>
                  </td>
                  <td className="py-3 pr-4">
                    <Badge
                      variant={org.role === 'admin' || org.role === 'owner' ? 'primary' : 'default'}
                    >
                      {org.role}
                    </Badge>
                  </td>
                  <td className="py-3">
                    <ActionDropdown
                      actions={[
                        {
                          label: 'Remove from Org',
                          variant: 'danger',
                          onClick: () => setRemoveTarget({ orgId: org.id, orgName: org.name }),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="py-8 text-center text-text-muted">
                  Not a member of any organization
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ForceAssignModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        userId={userId}
        userName={userName}
      />

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) {
            removeFromOrg.mutate(
              { userId, orgId: removeTarget.orgId },
              { onSuccess: () => setRemoveTarget(null) },
            );
          }
        }}
        title="Remove from Organization"
        message={`Remove user from ${removeTarget?.orgName ?? 'this organization'}?`}
        confirmLabel="Remove"
        confirmVariant="danger"
        loading={removeFromOrg.isPending}
      />
    </div>
  );
}

function UserSettingsTab({
  userId,
  user,
  orgCount,
}: {
  userId: string;
  user: {
    fullName: string;
    email: string;
    role: string;
    platformRole: string | null;
    isActive: boolean;
  };
  orgCount: number;
}) {
  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);
  const [platformRole, setPlatformRole] = useState(user.platformRole ?? '');
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [prevUserId, setPrevUserId] = useState(userId);

  const updateUser = useUpdateUserFull();
  const resetPw = useResetPassword();
  const deleteUser = useDeleteUser();

  // Reset form state when the user prop changes (React 19 "adjust state on prop change" pattern).
  if (userId !== prevUserId) {
    setPrevUserId(userId);
    setFullName(user.fullName);
    setEmail(user.email);
    setRole(user.role);
    setPlatformRole(user.platformRole ?? '');
  }

  function handleSave(e: FormEvent) {
    e.preventDefault();
    updateUser.mutate({
      userId,
      data: {
        fullName,
        email,
        role,
        platformRole: platformRole || null,
      },
    });
  }

  return (
    <form onSubmit={handleSave} className="space-y-4 max-w-lg">
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Full Name</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Role</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
        >
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="sales_rep">Sales Rep</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Platform Role</label>
        <select
          value={platformRole}
          onChange={(e) => setPlatformRole(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">Regular</option>
          <option value="platform_admin">Platform Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={updateUser.isPending}
            className="px-5 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50"
          >
            {updateUser.isPending ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmReset(true)}
            className="px-4 py-2 text-sm rounded-lg text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
          >
            Reset Password
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setConfirmToggle(true)}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              user.isActive
                ? 'text-danger hover:bg-danger/10'
                : 'text-success hover:bg-success/10'
            }`}
          >
            {user.isActive ? 'Disable' : 'Enable'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="px-4 py-2 text-sm rounded-lg text-danger hover:bg-danger/10 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmToggle}
        onClose={() => setConfirmToggle(false)}
        onConfirm={() => {
          updateUser.mutate(
            { userId, data: { isActive: !user.isActive } },
            { onSuccess: () => setConfirmToggle(false) },
          );
        }}
        title={user.isActive ? 'Disable User' : 'Enable User'}
        message={
          user.isActive
            ? 'This user will not be able to log in until re-enabled.'
            : 'This user will be able to log in again.'
        }
        confirmLabel={user.isActive ? 'Disable' : 'Enable'}
        confirmVariant={user.isActive ? 'danger' : 'primary'}
        loading={updateUser.isPending}
      />
      <ConfirmDialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => {
          resetPw.mutate(userId, { onSuccess: () => setConfirmReset(false) });
        }}
        title="Reset Password"
        message="A password reset email will be sent to the user."
        confirmLabel="Send Reset Email"
        loading={resetPw.isPending}
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          deleteUser.mutate(userId, {
            onSuccess: () => navigate('/users'),
          });
        }}
        title="Delete User"
        message={
          orgCount > 0
            ? `This user belongs to ${orgCount} organization${orgCount > 1 ? 's' : ''}. They will be removed from all organizations and permanently deleted. This action cannot be undone.`
            : 'This will permanently delete this user. This action cannot be undone.'
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteUser.isPending}
      />
    </form>
  );
}
